import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { ArrowLongRightIcon } from '../components/icons/Icons.jsx';

export default function Auth() {
  const { t } = useTranslation();
  const { isSignedIn, loading, signInWithEmail } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  if (!loading && isSignedIn) {
    const target = location.state?.from?.pathname ?? '/profile';
    return <Navigate to={target} replace />;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmail(email.trim());
      setSent(true);
    } catch (err) {
      setError(err.message ?? String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-md px-8 pb-20 pt-32">
      <div className="cta-box">
        <div className="section-label">{t('auth.label')}</div>
        <h1 className="cta-title">{t('auth.title')}</h1>
        <p className="cta-body">{t('auth.description')}</p>

        {sent ? (
          <div className="rounded-md border border-gold/30 bg-navy-mid/30 p-6 text-left">
            <h2 className="font-serif text-xl text-gold-light">{t('auth.sent.title')}</h2>
            <p className="mt-2 text-sm text-ink-secondary">
              {t('auth.sent.body', { email })}
            </p>
            <button
              type="button"
              className="mt-4 text-sm font-medium text-gold hover:text-gold-light"
              onClick={() => {
                setSent(false);
                setEmail('');
              }}
            >
              {t('auth.sent.useDifferentEmail')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <label className="block text-left">
              <span className="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
                {t('auth.emailLabel')}
              </span>
              <input
                type="email"
                required
                autoFocus
                autoComplete="email"
                placeholder={t('auth.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-white/10 bg-navy-deep/60 px-4 py-2.5 text-cream placeholder:text-ink-muted focus:border-gold/50"
              />
            </label>

            {error && (
              <p className="rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-left text-sm text-red-200">
                {error}
              </p>
            )}

            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || !email.trim()}
            >
              {submitting ? t('common.loading') : t('auth.send')}
              <ArrowLongRightIcon style={{ width: 18, height: 18 }} />
            </button>

            <p className="text-xs text-ink-muted">{t('auth.hint')}</p>
          </form>
        )}
      </div>
    </div>
  );
}
